'use client';

import { FC, useState } from 'react';
import { useTranslations } from '@/hooks/useTranslations';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { useSubmitStatus } from '@/hooks/useSubmitStatus';
import { deleteUser } from '@/actions/account/delete-user';
import { signOut } from 'next-auth/react';
import { toast } from 'sonner';
import { LoadingButton } from '@/components/loading-button';

interface Props {
  userId: string;
  userEmail: string;
}

export const DeleteAccountSection: FC<Props> = ({ userId, userEmail }) => {
  const t = useTranslations('UserSettings');
  const [open, setOpen] = useState(false);
  const [confirmEmail, setConfirmEmail] = useState('');
  const { status, wrap } = useSubmitStatus();

  const isConfirmed = confirmEmail === userEmail;

  const handleDelete = () => {
    if (!isConfirmed) return;

    wrap(async () => {
      const result = await deleteUser(userId);
      if (result.error) {
        toast.error(result.error);
        throw new Error(result.error);
      }
      toast.success(t('accountDeleted'));
      await signOut({ callbackUrl: '/' });
    });
  };

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-destructive">
        {t('dangerZone')}
      </h3>
      <p className="text-sm text-muted-foreground mt-1 mb-4">
        {t('deleteAccountDescription')}
      </p>
      <Button variant="destructive" onClick={() => setOpen(true)}>
        {t('deleteAccount')}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('deleteAccountConfirmTitle')}</DialogTitle>
            <DialogDescription>
              {t('deleteAccountConfirmDescription')}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-4">
            <Label htmlFor="confirm-email">
              {t('typeEmailToConfirm')} <strong>{userEmail}</strong>
            </Label>
            <Input
              id="confirm-email"
              type="email"
              value={confirmEmail}
              onChange={(e) => setConfirmEmail(e.target.value)}
              placeholder={userEmail}
            />
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              {t('cancel')}
            </Button>
            <LoadingButton
              type="button"
              status={status}
              idleText={t('deleteAccount')}
              loadingText={t('deleting')}
              successText={t('deleted')}
              errorText="Failed"
              onClick={handleDelete}
              isFull={false}
              disabled={!isConfirmed}
            />
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
